import { useMemo, useCallback, useRef, forwardRef } from 'react';
import cc from 'classnames';
import { observer } from 'decorators';
import { useDisposable, useCombineRefs, combineRefs } from 'hooks';
import Dropzone from 'react-dropzone';
import { Typography, Grid, IconButton, CircularProgress, Card, CardContent, CardActions, InputAdornment } from '@material-ui/core';
import Modal from 'components/modal';
import Input from 'components/form/input';
import Image from 'components/image';
import Video from 'components/video';
import Button from 'components/button';
import { SettingsItem } from 'components/settings';
import Store, { DropzoneEditFileStore } from './dropzone.store';
import media from 'services/media';
import { toFileSize, toDurationStr } from 'utils/number';
import { acceptVideoTypes, acceptAllTypes } from 'utils/dom';
import { ErrorRelatedAction } from 'app';

import { Delete, FileOutline, Alert, Download, Plus } from 'mdi-material-ui';

import useStyles from './dropzone.styles';

export default observer(forwardRef(function DropzoneView(props, ref) {
  const store = useDisposable(() => new Store(props));
  const classes = useStyles();
  const dropzone = useRef(null);

  const {
    autoUpload, className, children, title, nameOnly, hideCategory, hideDate, hideDescription, baseUrl, onUploaded, onUploading, onAllUploaded, onError,
    onManualFilesHandle, maxSize, multiple, disableClick, onClick, containerRef, accept, minHeight, isVideo, ...other
  } = props;
  const aMazSize = maxSize || (isVideo ? null : 60000000);
  const aMultiple = multiple !== false;

  const opts = useMemo(() => !disableClick && onClick ? { onClick } : undefined, [ disableClick, onClick ]);
  const handleDrop = useCallback((f, r) => store.onDrop(f, r, autoUpload), [ store, autoUpload ]);
  const syncRef = useCombineRefs(dropzone, ref);

  const handleUpload = useCallback(() => dropzone.current && dropzone.current.open(), []);
  const innerComponent = useCallback(({ getRootProps, getInputProps, isDragActive }) => {
    const { ref: propRef, ...otherProps } = getRootProps(opts);
    return <div ref={combineRefs(containerRef, propRef)} {...otherProps} className={cc(classes.container, className, isDragActive && classes.onfiles)} style={minHeight && { minHeight: '100%' }}>
      <input hidden {...getInputProps()} />
      { !autoUpload &&
        <DropzoneUploadView
          store={store}
          title={title}
          nameOnly={nameOnly}
          hideCategory={hideCategory}
          hideDate={hideDate}
          hideDescription={hideDescription}
          onUpload={handleUpload}
          multiple={aMultiple}
        />
      }
      { children }
    </div>;
  }, [ aMultiple, autoUpload, children, className, classes, nameOnly, handleUpload, store, opts, title, hideDescription, hideCategory, hideDate, minHeight, containerRef ]);

  return (
    <Dropzone
      noDragEventsBubbling
      noClick={disableClick}
      onDrop={handleDrop}
      ref={syncRef}
      maxSize={aMazSize}
      multiple={aMultiple}
      // Default to all types so that options to instantly capture photo etc are still present
      accept={accept || (isVideo ? acceptVideoTypes : acceptAllTypes)}
      {...other}
    >
      { innerComponent }
    </Dropzone>
  );
}));

export const DropzoneFileEditView = observer(function DropzoneFileEditView(props) {
  const store = useDisposable(() => new DropzoneEditFileStore(props));
  const classes = useStyles();

  const { form, isSaving, ext, thumbnail } = store;
  const { title, nameOnly } = props;
  const isSmall = media.is('xs');

  const handleClose = useCallback(() => store.close(), [ store ]);
  const handleDownload = useCallback(() => store.download(), [ store ]);
  const handleDelete = useCallback(() => store.delete(), [ store ]);
  const handleSave = useCallback(() => store.save(), [ store ]);

  return <Modal
    open
    label={title || 'Edit Upload'}
    onClose={handleClose}
  >
    <Grid container spacing={2} direction="column">
      <Grid item>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4} style={{ position: 'relative' }}>
            <div className={cc(isSmall && classes.imageContainer)}>
              <div className={classes.imageContainerInner}>
                <Image className={classes.fileImage} file={thumbnail} fallbackComponent={<FileOutline className={classes.fallback} />} alt="File Preview" />
              </div>
              <IconButton className={classes.downloadIcon} onClick={handleDownload} aria-label="Download"><Download /></IconButton>
            </div>
          </Grid>
          <Grid item xs={12} sm={8}>
            <Grid container spacing={isSmall ? 0 : 2}>
              <Grid item xs={12} sm={nameOnly ? 12 : 6}>
                <Input form={form} field="name" endAdornment={<InputAdornment position="end">{ext}</InputAdornment>} />
              </Grid>
              { !nameOnly &&
                <Grid item xs={12} sm={6}>
                  <Input form={form} field="date" />
                </Grid>
              }
            </Grid>
            { !nameOnly && <Input form={form} field="category" /> }
            { !nameOnly && <Input form={form} field="description" /> }
          </Grid>
        </Grid>
      </Grid>
      <Grid item>
        <Grid container>
          <Grid item>
            <Button color="secondary" onClick={handleDelete} isLoading={isSaving}>Delete</Button>
          </Grid>
          <Grid item xs />
          <Grid item>
            <Button onClick={handleClose}>Cancel</Button>&nbsp;&nbsp;
            <Button variant="contained" color="primary" onClick={handleSave} isLoading={isSaving}>Save</Button>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  </Modal>;
});

const DropzoneUploadView = observer(function DropzoneUploadView({ store, title, buttonTitle, nameOnly, hideCategory, hideDate, hideDescription, onUpload: handleUpload, multiple }) {
  const handleReset = useCallback(() => store.reset(), [ store ]);
  const handleUploadFiles = useCallback(() => store.uploadFiles(), [ store ]);

  return <Modal
    label={title || 'Upload Files'}
    open={!!store.droppedFiles.length}
    onClose={handleReset}
  >
    <Grid container direction="column" spacing={2}>
      { !nameOnly && !hideCategory &&
        <Grid item>
          <Input form={store.form} field="category" />
        </Grid>
      }
      <Grid item>
        <Grid container spacing={2}>
          { store.droppedFiles.map(f => <DropzoneFileView key={f.id} store={store} file={f} nameOnly={nameOnly} hideDate={hideDate} hideDescription={hideDescription} />) }
        </Grid>
      </Grid>
      <Grid item>
        <Grid container spacing={2}>
          { multiple &&
            <Grid item>
              <Button color="secondary" onClick={handleUpload}><Plus />&nbsp;&nbsp;Add More</Button>
            </Grid>
          }
          <Grid item xs />
          <Grid item>
            <Button color="primary" onClick={handleUploadFiles} isLoading={store.isUploading}>
              { buttonTitle || 'Scan for viruses and save' }
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  </Modal>;
});

const DropzoneFileView = observer(function DropzoneFileView({ store, file, nameOnly, hideDate, hideDescription }) {
  const classes = useStyles();
  const handleRemove = useCallback(() => store.removeFile(file), [ store, file ]);
  const handleSetDuration = useCallback(d => file.setDuration(d), [ file ]);

  const { isVideo, preview, uploadProgress, errorMessage, errorLink, file: f, form, isUploading, processing, ext, duration } = file;

  const fallbackComponent = <FileOutline className={classes.fallback} />;

  return <Grid item xs={12} md={6}>
    <Card>
      <div className={classes.imageContainer}>
        <div className={classes.imageContainerInner}>
          { isVideo && <Video className={classes.fileImage} src={preview} fallbackComponent={fallbackComponent} onDurationChange={handleSetDuration} /> }
          { !isVideo && <Image className={classes.fileImage} src={preview} fallbackComponent={fallbackComponent} alt="File Preview" /> }
          { uploadProgress != null &&
            <div className={classes.progressOverlay}>
              <Grid container direction="column" alignItems="center">
                <Grid item>
                  <CircularProgress size={80} color="secondary" variant={uploadProgress ? 'determinate' : undefined} value={uploadProgress} />
                </Grid>
                <Grid item>
                  <Typography variant="subtitle1" color="secondary">
                    { processing ? 'Processing...' : 'Uploading...' }
                  </Typography>
                </Grid>
              </Grid>
            </div>
          }
        </div>
      </div>
      <CardContent>
        { errorMessage &&
          <SettingsItem
            error
            open
            fullWidth
            className={classes.errorMessage}
            title={
              <Typography color="error">
                <Alert /> { errorMessage || 'Upload failed. Please try again' }
              </Typography>
            }
          >
            <Typography align="right">
              <ErrorRelatedAction linkName={errorLink} />
            </Typography>
          </SettingsItem>
        }
        { !duration &&
          <Typography>
            File Size: {toFileSize(f.size)}
          </Typography>
        }
        { !!duration &&
          <Typography>
            Video Length: {toDurationStr(duration)}
          </Typography>
        }
        <Input
          form={form} field="name"
          readOnly={isUploading}
          endAdornment={<InputAdornment position="end">{ext}</InputAdornment>}
        />
        { !nameOnly && !hideDate && <Input form={form} field="date" readOnly={isUploading} /> }
        { !nameOnly && !hideDescription && <Input form={form} field="description" readOnly={isUploading} /> }
      </CardContent>
      <CardActions>
        <IconButton onClick={handleRemove} disabled={isUploading} aria-label="Remove">
          <Delete />
        </IconButton>
      </CardActions>
    </Card>
  </Grid>;
});